// Sidebar navigation component
import { useNavigate, Link } from 'react-router-dom';
import React, { startTransition } from 'react';
import { preloadRoute } from '@utils/preloadRoute';
import {
  LayoutDashboard,
  Cloud,
  Share2,
  CreditCard,
  Settings,
  LifeBuoy,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Upload,
  Youtube,
  HardDrive,
  BadgeCheck,
} from 'lucide-react';
import { Logo } from '@components/ui/Logo';
import { Divider } from '@components/ui/Divider';
import { SidebarNavButton } from './SidebarNavButton';
import { SidebarTooltip } from './SidebarTooltip';
import { SidebarPopoverPanel } from './SidebarPopoverPanel';
import { SidebarStorageCard } from './SidebarStorageCard';
import { SidebarPlanCard } from './SidebarPlanCard';
import { cn } from '@utils/classNames';
import { ROUTES } from '@config/constants';
import { PLANS, type PlanType } from '@config/plans';
import { useAuthStore } from '@stores/authStore';
import { useStableAuthValues } from './StableAuthValues';
import { useToast } from '@components/ui/Toast';
import { useFileStore } from '@stores/useFileStore';
import { formatDateTime } from '@utils/format';
import { useSidebar } from './SidebarContext';
import { UserPlan } from '@types';
import { useQuickActions } from '@hooks/useQuickActions';
import { useLocationContext } from './LocationContext';
import { useStableUser } from '@hooks/useStableUser';
import { fetchBillingOverview, type BillingOverviewResponse } from '@services/billing.service';
import { useSidebarMobileGestures } from '@hooks/useSidebarMobileGestures';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: ROUTES.DASHBOARD, icon: <LayoutDashboard /> },
  { label: 'Cloud Explorer', path: ROUTES.CLOUD_EXPLORER, icon: <Cloud /> },
  { label: 'Sharing', path: ROUTES.SHARING, icon: <Share2 /> },
];

const SidebarComponent: React.FC = () => {
  // Use stable auth values that only change when values actually change
  // This prevents re-renders when store updates but values remain the same
  const { isLoading, planReady } = useStableAuthValues();
  const stableUser = useStableUser();
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  // Subscribe to pathname changes via LocationContext (no polling – efficient)
  const { getPathname, subscribe } = useLocationContext();
  const [pathname, setPathname] = React.useState(() => getPathname());

  React.useEffect(() => {
    setPathname(getPathname());
    const unsubscribe = subscribe((nextPathname) => setPathname(nextPathname));
    return unsubscribe;
  }, [getPathname, subscribe]);
  // Use selective selector to prevent re-renders when files array reference changes
  // Only subscribe to files.length for totalFiles calculation
  const totalFiles = useFileStore((state) => state.files.length);
  const { isOpen, closeSidebar, isCollapsed, toggleCollapse, openSidebar } = useSidebar();
  const { handleUpload } = useQuickActions();
  const toast = useToast();
  const {
    isDragging,
    showBackdrop,
    sidebarStyle,
    backdropStyle,
    panelTouchHandlers,
  } = useSidebarMobileGestures(isOpen, openSidebar, closeSidebar);
  
  // CRITICAL: Stabilize isLoading to prevent flicker on tab switch
  // Once loading is complete, keep it false even if it briefly becomes true
  const stableIsLoading = React.useRef(false);
  React.useEffect(() => {
    if (!isLoading) {
      stableIsLoading.current = false;
    } else if (stableUser) {
      // If we have a stable user, don't show loading state
      stableIsLoading.current = false;
    }
  }, [isLoading, stableUser]);
  // CRITICAL: If we have a stable user, never show loading state
  const isActuallyLoading = stableUser ? false : (isLoading && stableIsLoading.current === false ? false : isLoading);
  
  // Convert UserPlan enum to PlanType string for PLANS lookup
  const planId = (stableUser?.plan ?? UserPlan.FREE) as UserPlan;
  const planTypeString = planId.toLowerCase() as PlanType;
  const plan = planTypeString in PLANS ? PLANS[planTypeString] : null;
  
  // Fetch billing overview for accurate next billing date
  const [billingOverview, setBillingOverview] = React.useState<BillingOverviewResponse | null>(null);
  
  React.useEffect(() => {
    const fetchBilling = async () => {
      if (!stableUser?.id && !stableUser?.email) {
        return;
      }
      
      try {
        const data = await fetchBillingOverview({
          userId: stableUser?.id,
          email: stableUser?.email,
        });
        setBillingOverview(data);
      } catch (error) {
        // Silently fail - will fallback to user data from auth store
        console.debug('Failed to fetch billing overview in Sidebar:', error);
      }
    };
    
    // Fetch billing for all logged-in users so we can show correct status (e.g. "Free" after subscription ends)
    if (stableUser?.id || stableUser?.email) {
      fetchBilling();
    }
  }, [stableUser?.id, stableUser?.email]);
  
  // Hide upgrade button if user has at least Bedroom Plan
  // Only show upgrade button if user data is fully loaded (not loading) and user is on FREE plan
  // CRITICAL: Use stableUser and stableIsLoading to prevent flicker on tab switch. planReady verhindert FREE-Flackern.
  const shouldShowUpgrade = React.useMemo(() => {
    if (!planReady) return false;
    if (isActuallyLoading) return false;
    if (!stableUser) return false;
    if (!stableUser.id) return false;
    return stableUser.plan === UserPlan.FREE;
  }, [planReady, isActuallyLoading, stableUser?.id, stableUser?.plan]);

  const renewalText = React.useMemo(() => {
    if (!stableUser) {
      return null;
    }

    const isFreePlan = planId === UserPlan.FREE || billingOverview?.plan === 'free';
    if (isFreePlan) {
      return 'Free account - no automatic renewal';
    }

    const planExpiresAt = billingOverview?.planExpiresAt ?? stableUser.planExpiresAt ?? null;
    if (planExpiresAt) {
      const formattedExpiry = formatDateTime(planExpiresAt);
      return formattedExpiry && formattedExpiry !== 'Invalid date'
        ? `Complimentary access until ${formattedExpiry} (set by support, not a subscription renewal)`
        : 'Temporary plan active — expiry syncing…';
    }

    // Prefer billing overview data (synced from Stripe) over user store data
    const subscriptionRenewsAt = billingOverview?.subscriptionRenewsAt ?? stableUser.subscriptionRenewsAt;
    const subscriptionStatus = billingOverview?.subscriptionStatus ?? stableUser.subscriptionStatus ?? 'active';
    const subscriptionCancelAtPeriodEnd = billingOverview?.subscriptionCancelAtPeriodEnd ?? false;

    const rawDate = subscriptionRenewsAt;
    const parsedDate = rawDate ? new Date(rawDate) : null;
    const hasValidDate = parsedDate && !Number.isNaN(parsedDate.getTime());

    if (!hasValidDate) {
      if (subscriptionCancelAtPeriodEnd || subscriptionStatus === 'canceled' || subscriptionStatus === 'incomplete' || subscriptionStatus === 'incomplete_expired') {
        return 'Cancellation scheduled - access ends after the current billing period';
      }
      return 'Next billing date syncing...';
    }

    const formattedDate = formatDateTime(parsedDate);

    if (subscriptionCancelAtPeriodEnd || subscriptionStatus === 'canceled' || subscriptionStatus === 'incomplete' || subscriptionStatus === 'incomplete_expired') {
      return `Ends on ${formattedDate}`;
    }

    return `Renews on ${formattedDate}`;
  }, [
    planId,
    stableUser,
    billingOverview?.plan,
    billingOverview?.planExpiresAt,
    billingOverview?.subscriptionRenewsAt,
    billingOverview?.subscriptionStatus,
    billingOverview?.subscriptionCancelAtPeriodEnd,
  ]);

  const handleLogout = async () => {
    try {
      await logout();
      // Navigation is handled by authStore.signOut()
      toast.success('Logged out', 'You have been successfully logged out.');
    } catch (error: any) {
      toast.error('Logout failed', error.message || 'An error occurred while logging out.');
    }
  };

  const storagePercentage = stableUser 
    ? Math.round((stableUser.storageUsed / stableUser.storageLimit) * 100) 
    : 0;
  
  // Debug logging for storage values
  React.useEffect(() => {
    if (stableUser && stableUser.storageUsed === 0 && stableUser.storageLimit > 0) {
      console.log('🔍 [Sidebar] Storage values:', {
        used: stableUser.storageUsed,
        limit: stableUser.storageLimit,
        percentage: storagePercentage,
        userId: stableUser.id
      });
    }
  }, [stableUser?.storageUsed, stableUser?.storageLimit, storagePercentage, stableUser?.id]);

  const isSettingsRoute = React.useMemo(
    () =>
      [ROUTES.SETTINGS, ROUTES.BILLING].some((route) =>
        pathname.startsWith(route)
      ),
    [pathname]
  );

  const isSharingRoute = React.useMemo(
    () =>
      [ROUTES.SHARING, ROUTES.YOUTUBE].some((route) =>
        pathname.startsWith(route)
      ),
    [pathname]
  );

  // Initialize dropdown states based on current route (no persistence)
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(isSettingsRoute);
  const [isSharingOpen, setIsSharingOpen] = React.useState(isSharingRoute);

  // Update dropdown states when route changes
  React.useEffect(() => {
    setIsSettingsOpen(isSettingsRoute);
  }, [isSettingsRoute]);

  React.useEffect(() => {
    setIsSharingOpen(isSharingRoute);
  }, [isSharingRoute]);

  type SidebarPopoverId = 'storage' | 'plan';
  const [activePopover, setActivePopover] = React.useState<SidebarPopoverId | null>(
    null
  );
  const [pinnedPopover, setPinnedPopover] = React.useState<SidebarPopoverId | null>(
    null
  );
  const pinnedPopoverRef = React.useRef<SidebarPopoverId | null>(null);
  pinnedPopoverRef.current = pinnedPopover;
  const popoverLeaveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const storageTriggerRef = React.useRef<HTMLButtonElement>(null);
  const planTriggerRef = React.useRef<HTMLButtonElement>(null);

  const clearPopoverLeaveTimer = React.useCallback(() => {
    if (popoverLeaveTimerRef.current) {
      clearTimeout(popoverLeaveTimerRef.current);
      popoverLeaveTimerRef.current = null;
    }
  }, []);

  const openPopover = React.useCallback(
    (id: SidebarPopoverId) => {
      clearPopoverLeaveTimer();
      setActivePopover(id);
    },
    [clearPopoverLeaveTimer]
  );

  const scheduleClosePopover = React.useCallback(() => {
    clearPopoverLeaveTimer();
    popoverLeaveTimerRef.current = setTimeout(() => {
      const pinned = pinnedPopoverRef.current;
      setActivePopover((current) => {
        if (pinned && current === pinned) {
          return current;
        }
        return null;
      });
    }, 150);
  }, [clearPopoverLeaveTimer]);

  const handlePopoverClick = React.useCallback(
    (id: SidebarPopoverId) => {
      clearPopoverLeaveTimer();
      if (pinnedPopover === id) {
        setPinnedPopover(null);
        setActivePopover(null);
      } else {
        setPinnedPopover(id);
        setActivePopover(id);
      }
    },
    [clearPopoverLeaveTimer, pinnedPopover]
  );

  const closePopover = React.useCallback(() => {
    clearPopoverLeaveTimer();
    setPinnedPopover(null);
    setActivePopover(null);
  }, [clearPopoverLeaveTimer]);

  React.useEffect(() => {
    if (!isCollapsed) {
      closePopover();
    }
  }, [isCollapsed, closePopover]);

  React.useEffect(() => () => clearPopoverLeaveTimer(), [clearPopoverLeaveTimer]);

  const navigateWithPreload = React.useCallback((path: string) => {
    // Preload on real navigation too (not only hover) to help touch/fast clicks.
    preloadRoute(path);
    setTimeout(() => {
      startTransition(() => {
        navigate(path);
        closeSidebar();
      });
    }, 0);
  }, [navigate, closeSidebar]);

  return (
    <>
      {/* Backdrop for mobile - Clean - NO BLUR so sidebar isn't blurred */}
      {showBackdrop && (
        <div
          onClick={() => {
            startTransition(() => {
              closeSidebar();
            });
          }}
          {...panelTouchHandlers}
          style={backdropStyle}
          className={cn(
            'fixed inset-0 bg-black/60 z-[90] md:hidden touch-pan-y',
            isDragging ? 'transition-none' : 'transition-opacity duration-300'
          )}
        />
      )}

      {/* Sidebar - Clean mobile design - visible on 360px and above */}
      <aside
        {...panelTouchHandlers}
        style={sidebarStyle}
        data-sidebar-collapsed={isCollapsed ? 'true' : 'false'}
        className={cn(
        'fixed top-0 left-0 w-[min(260px,85vw)] sm:w-[min(280px,85vw)] bg-dark-tertiary/98 backdrop-blur-md border-r border-white/[0.08] flex flex-col',
        isDragging ? 'transition-none' : 'transition-[transform,width] duration-300 ease-out',
        'h-[100dvh] max-h-[100dvh] pt-[env(safe-area-inset-top,0px)]',
        'overflow-y-auto overflow-x-hidden',
        'overscroll-contain touch-pan-y',
        'z-[95]',
        !isDragging && (isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'),
        isCollapsed ? 'md:max-w-[72px]' : 'md:max-w-[300px]'
      )}>
      {/* Logo */}
      <div
        className={cn(
          'mt-4 sm:mt-6 mb-4 sm:mb-6 flex items-center bg-transparent',
          isCollapsed ? 'px-2 justify-center' : 'px-4 sm:px-6'
        )}
      >
        <Link
          to={ROUTES.DASHBOARD}
          className={cn(
            'cursor-pointer hover:opacity-80 transition-opacity block',
            isCollapsed ? 'w-auto' : 'w-full'
          )}
          aria-label="Go to dashboard"
        >
          <Logo
            size={isCollapsed ? 'small' : 'medium'}
            showSubtext={false}
            variant={isCollapsed ? 'icon' : 'full'}
          />
        </Link>
      </div>

      {!isCollapsed && <Divider className="mb-4" />}

      {/* Navigation */}
      <nav className={cn('space-y-2', isCollapsed ? 'px-2' : 'px-5')}>
        {navItems.filter((item) => item.path !== ROUTES.SHARING).map((item) => {
          const isActive = pathname === item.path;
          return (
            <SidebarNavButton
              key={item.path}
              label={item.label}
              isActive={isActive}
              collapsed={isCollapsed}
              icon={item.icon}
              onMouseEnter={() => preloadRoute(item.path)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateWithPreload(item.path);
              }}
            />
          );
        })}

        {/* Sharing */}
        {isCollapsed ? (
          <>
            <SidebarNavButton
              label="Beat Packs"
              isActive={pathname === ROUTES.SHARING}
              collapsed
              icon={<Share2 />}
              onMouseEnter={() => preloadRoute(ROUTES.SHARING)}
              onClick={(e) => {
                e.preventDefault();
                navigateWithPreload(ROUTES.SHARING);
              }}
            />
            <SidebarNavButton
              label="YouTube"
              isActive={pathname === ROUTES.YOUTUBE}
              collapsed
              icon={<Youtube />}
              onMouseEnter={() => preloadRoute(ROUTES.YOUTUBE)}
              onClick={(e) => {
                e.preventDefault();
                navigateWithPreload(ROUTES.YOUTUBE);
              }}
            />
          </>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setIsSharingOpen((prev) => !prev)}
              className={cn(
                'w-full flex items-center justify-between h-[42px] px-4 rounded-xl transition-all duration-200',
                isSharingRoute
                  ? 'bg-white/12'
                  : 'opacity-75 hover:opacity-100 hover:bg-white/[0.06]'
              )}
              aria-expanded={isSharingOpen}
              aria-controls="sidebar-sharing-group"
            >
              <div className="flex items-center gap-3">
                <div className={cn(isSharingRoute ? 'text-white' : 'text-white/70')}>
                  <Share2 className="w-5 h-5" />
                </div>
                <span
                  className={cn(
                    'font-codec font-bold text-base',
                    isSharingRoute ? 'text-white' : 'text-white/70'
                  )}
                >
                  Sharing
                </span>
              </div>
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 transition-transform duration-200',
                  isSharingRoute ? 'text-white/60' : 'text-white/40',
                  isSharingOpen && 'rotate-180'
                )}
              />
            </button>
            <div
              id="sidebar-sharing-group"
              className={cn(
                'grid transition-[grid-template-rows] duration-300 ease-in-out',
                isSharingOpen ? 'grid-rows-[1fr] mt-1.5' : 'grid-rows-[0fr]'
              )}
            >
              <div className="overflow-hidden flex flex-col gap-1.5 px-2.5">
                <button
                  type="button"
                  onMouseEnter={() => preloadRoute(ROUTES.SHARING)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigateWithPreload(ROUTES.SHARING);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 h-[36px] rounded-md px-3 text-sm font-codec font-bold transition-all text-left',
                    pathname === ROUTES.SHARING
                      ? 'bg-white/15 text-white'
                      : 'text-white/60 hover:text-white/90 hover:bg-white/5'
                  )}
                >
                  <span className="flex items-center justify-center">
                    <Share2 className="w-4 h-4" />
                  </span>
                  Beat Packs
                </button>
                <button
                  type="button"
                  onMouseEnter={() => preloadRoute(ROUTES.YOUTUBE)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigateWithPreload(ROUTES.YOUTUBE);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 h-[36px] rounded-md px-3 text-sm font-codec font-bold transition-all text-left',
                    pathname === ROUTES.YOUTUBE
                      ? 'bg-white/15 text-white'
                      : 'text-white/60 hover:text-white/90 hover:bg-white/5'
                  )}
                >
                  <span className="flex items-center justify-center">
                    <Youtube className="w-4 h-4" />
                  </span>
                  YouTube
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings */}
        {isCollapsed ? (
          <>
            <SidebarNavButton
              label="Settings"
              isActive={pathname === ROUTES.SETTINGS}
              collapsed
              icon={<Settings />}
              onMouseEnter={() => preloadRoute(ROUTES.SETTINGS)}
              onClick={(e) => {
                e.preventDefault();
                navigateWithPreload(ROUTES.SETTINGS);
              }}
            />
            <SidebarNavButton
              label="Billing"
              isActive={pathname === ROUTES.BILLING}
              collapsed
              icon={<CreditCard />}
              onMouseEnter={() => preloadRoute(ROUTES.BILLING)}
              onClick={(e) => {
                e.preventDefault();
                navigateWithPreload(ROUTES.BILLING);
              }}
            />
          </>
        ) : (
          <div className="-mt-4">
            <button
              type="button"
              onClick={() => setIsSettingsOpen((prev) => !prev)}
              className={cn(
                'w-full flex items-center justify-between h-[42px] px-4 rounded-xl transition-all duration-200',
                isSettingsRoute
                  ? 'bg-white/12'
                  : 'opacity-75 hover:opacity-100 hover:bg-white/[0.06]'
              )}
              aria-expanded={isSettingsOpen}
              aria-controls="sidebar-settings-group"
            >
              <div className="flex items-center gap-3">
                <div className={cn(isSettingsRoute ? 'text-white' : 'text-white/70')}>
                  <Settings className="w-5 h-5" />
                </div>
                <span
                  className={cn(
                    'font-codec font-bold text-base',
                    isSettingsRoute ? 'text-white' : 'text-white/70'
                  )}
                >
                  Settings
                </span>
              </div>
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 transition-transform duration-200',
                  isSettingsRoute ? 'text-white/60' : 'text-white/40',
                  isSettingsOpen && 'rotate-180'
                )}
              />
            </button>
            <div
              id="sidebar-settings-group"
              className={cn(
                'grid transition-[grid-template-rows] duration-300 ease-in-out mt-1.5',
                isSettingsOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              )}
            >
              <div className="overflow-hidden flex flex-col gap-1.5 px-2.5">
                <button
                  type="button"
                  onMouseEnter={() => preloadRoute(ROUTES.SETTINGS)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigateWithPreload(ROUTES.SETTINGS);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 h-[36px] rounded-md px-3 text-sm font-codec font-bold transition-all text-left',
                    pathname === ROUTES.SETTINGS
                      ? 'bg-white/15 text-white'
                      : 'text-white/60 hover:text-white/90 hover:bg-white/5'
                  )}
                >
                  <span className="flex items-center justify-center">
                    <Settings className="w-4 h-4" />
                  </span>
                  General
                </button>
                <button
                  type="button"
                  onMouseEnter={() => preloadRoute(ROUTES.BILLING)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigateWithPreload(ROUTES.BILLING);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 h-[36px] rounded-md px-3 text-sm font-codec font-bold transition-all text-left',
                    pathname === ROUTES.BILLING
                      ? 'bg-white/15 text-white'
                      : 'text-white/60 hover:text-white/90 hover:bg-white/5'
                  )}
                >
                  <span className="flex items-center justify-center">
                    <CreditCard className="w-4 h-4" />
                  </span>
                  Billing
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {!isCollapsed && <Divider className="mt-4" />}

      {stableUser && isCollapsed && (
        <div className="px-2 mt-3">
          <button
            ref={storageTriggerRef}
            type="button"
            onMouseEnter={() => openPopover('storage')}
            onMouseLeave={scheduleClosePopover}
            onClick={() => handlePopoverClick('storage')}
            className={cn(
              'flex h-10 w-full items-center justify-center rounded-xl border transition-colors',
              activePopover === 'storage'
                ? 'bg-white/12 border-white/20'
                : 'bg-white/[0.06] border-white/[0.08] hover:bg-white/[0.08]'
            )}
            aria-label="Storage"
            aria-expanded={activePopover === 'storage'}
          >
            <HardDrive className="w-5 h-5 text-white/80" />
          </button>
          <SidebarPopoverPanel
            isOpen={activePopover === 'storage'}
            onClose={closePopover}
            onPanelMouseEnter={clearPopoverLeaveTimer}
            onPanelMouseLeave={scheduleClosePopover}
            triggerRef={storageTriggerRef}
            ariaLabel="Storage details"
          >
            <SidebarStorageCard
              storageUsed={stableUser.storageUsed}
              storageLimit={stableUser.storageLimit}
              storagePercentage={storagePercentage}
              totalFiles={totalFiles}
            />
          </SidebarPopoverPanel>
        </div>
      )}

      {stableUser && !isCollapsed && (
        <SidebarStorageCard
          storageUsed={stableUser.storageUsed}
          storageLimit={stableUser.storageLimit}
          storagePercentage={storagePercentage}
          totalFiles={totalFiles}
          className="mx-5 mt-4"
        />
      )}

      {stableUser && isCollapsed && (
        <div className="px-2 mt-2">
          <button
            ref={planTriggerRef}
            type="button"
            onMouseEnter={() => openPopover('plan')}
            onMouseLeave={scheduleClosePopover}
            onClick={() => handlePopoverClick('plan')}
            className={cn(
              'flex h-10 w-full items-center justify-center rounded-xl border transition-colors',
              activePopover === 'plan'
                ? 'bg-white/12 border-white/20'
                : 'bg-white/[0.06] border-white/[0.08] hover:bg-white/[0.08]'
            )}
            aria-label="Your plan"
            aria-expanded={activePopover === 'plan'}
          >
            <BadgeCheck className="w-5 h-5 text-primary" />
          </button>
          <SidebarPopoverPanel
            isOpen={activePopover === 'plan'}
            onClose={closePopover}
            onPanelMouseEnter={clearPopoverLeaveTimer}
            onPanelMouseLeave={scheduleClosePopover}
            triggerRef={planTriggerRef}
            ariaLabel="Plan details"
          >
            <SidebarPlanCard
              planDisplayName={plan?.displayName ?? planTypeString.toUpperCase()}
              shouldShowUpgrade={shouldShowUpgrade}
              renewalText={renewalText}
              onUpgrade={() => {
                closePopover();
                startTransition(() => {
                  navigate('/pricing');
                  closeSidebar();
                });
              }}
            />
          </SidebarPopoverPanel>
        </div>
      )}

      {stableUser && !isCollapsed && (
        <SidebarPlanCard
          planDisplayName={plan?.displayName ?? planTypeString.toUpperCase()}
          shouldShowUpgrade={shouldShowUpgrade}
          renewalText={renewalText}
          onUpgrade={() => {
            startTransition(() => {
              navigate('/pricing');
              closeSidebar();
            });
          }}
          className="mx-5 mt-4"
        />
      )}

      {/* Spacer - pushes bottom content down */}
      <div className="flex-1" />

      {/* Bottom Actions */}
      <div
        className={cn(
          'pb-4 space-y-2.5 mt-auto flex-shrink-0',
          isCollapsed ? 'px-2' : 'px-5'
        )}
      >
        <button
          type="button"
          onClick={toggleCollapse}
          className={cn(
            'hidden md:flex w-full items-center justify-center gap-2 h-9 rounded-xl',
            'bg-white/5 text-white/70 hover:text-white hover:bg-white/10 transition-all border border-white/[0.08]'
          )}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <ChevronLeft className="w-4 h-4" />
              <span className="font-codec font-medium text-xs">Collapse</span>
            </>
          )}
        </button>

        {!isCollapsed && <Divider className="mb-3" />}

        {isCollapsed ? (
          <SidebarTooltip label="Quick Upload">
            <button
              type="button"
              onClick={() => {
                handleUpload();
                startTransition(() => closeSidebar());
              }}
              className="flex h-10 w-full items-center justify-center rounded-xl bg-primary text-black hover:opacity-90 transition-opacity active:scale-[0.98]"
              aria-label="Quick Upload"
            >
              <Upload className="w-4 h-4 text-black" />
            </button>
          </SidebarTooltip>
        ) : (
          <button
            type="button"
            onClick={() => {
              handleUpload();
              startTransition(() => closeSidebar());
            }}
            className="flex items-center justify-center gap-2 w-full h-10 bg-primary text-black rounded-xl hover:opacity-90 transition-opacity active:scale-[0.98]"
          >
            <Upload className="w-4 h-4 text-black" />
            <span className="font-codec font-bold text-sm text-black">Quick Upload</span>
          </button>
        )}

        <SidebarNavButton
          label="Support"
          collapsed={isCollapsed}
          variant="action"
          icon={<LifeBuoy />}
          onMouseEnter={() => preloadRoute(ROUTES.SUPPORT)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigateWithPreload(ROUTES.SUPPORT);
          }}
        />

        <SidebarNavButton
          label="Log out"
          collapsed={isCollapsed}
          variant="danger"
          icon={<LogOut />}
          onClick={() => {
            handleLogout();
            startTransition(() => closeSidebar());
          }}
        />
      </div>
    </aside>
    </>
  );
};

// Memoize Sidebar to prevent re-renders on route changes
// Memoize Sidebar - optimization comes from selective store selectors
// This memo prevents re-renders from parent, but store selectors prevent re-renders from store changes
export const Sidebar = React.memo(SidebarComponent);
Sidebar.displayName = 'Sidebar';